import React from 'react'
import { Show } from '@refinedev/antd'
import { useShow } from '@refinedev/core'
import { Link } from 'react-router'
import { Card, Skeleton, Space, Typography } from 'antd'
import { brand } from '../../theme/brand'
import { Pill } from '../../theme/listkit'
import { headStyle, Field, fmtDate } from '../reports/reportUiKit'
import { SELECT, type Suggestion } from './list'
import { statusLabel, statusTone } from './suggestionStatus'
import { useSuggestionAdminNote } from './adminNote'

/** Every card caps at 720px — the owner's display is 3440px wide, and an
 * uncapped card throws its content to the far edge (the admin-ultrawide rule). */
const CARD: React.CSSProperties = { marginBottom: 16, maxWidth: 720 }

const muted: React.CSSProperties = { fontFamily: brand.fSans, fontSize: 13, color: brand.sub, fontStyle: 'italic' }

/**
 * Suggestion — read-only detail. Triage (status + note) is the Edit page; there
 * is NO Delete here or anywhere (no admin DELETE policy on suggestions).
 *
 * "View profile" links to /profiles/show/<author.id> — profiles.id from the FK
 * embed. The row's user_id is a DIFFERENT uuid in every row, and ProfileShow
 * resolves profiles.id, so a link built from user_id opens an empty profile
 * (the trap bf6cc345 fixed in four other admin links).
 *
 * The private note is read from suggestion_admin_notes (see adminNote.ts for why
 * it is a separate table) and labelled as admin-only right in the card title.
 */
export function SuggestionShow() {
  const { query } = useShow<Suggestion>({ meta: { select: SELECT } })
  const { data, isLoading } = query
  const record = data?.data
  const noteState = useSuggestionAdminNote(record?.id)

  return (
    <Show isLoading={isLoading} canDelete={false}>
      <Card title={<span style={headStyle}>The suggestion</span>} style={CARD}>
        <Field label="From">
          <Space size={10} wrap>
            <span>
              {record?.author?.name ?? 'Unknown member'}
              {record?.author?.class_year ? ` · Class of ${record.author.class_year}` : ''}
            </span>
            {record?.author?.id ? (
              <Link to={`/profiles/show/${record.author.id}`} data-testid="suggestion-view-profile">
                View profile
              </Link>
            ) : null}
          </Space>
        </Field>
        <Field label="Status">
          {record ? <Pill tone={statusTone(record.status)}>{statusLabel(record.status)}</Pill> : '—'}
        </Field>
        <Field label="Sent">{fmtDate(record?.created_at)}</Field>
        <Field label="Suggestion">
          {record?.body ? (
            <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>{record.body}</Typography.Paragraph>
          ) : (
            '—'
          )}
        </Field>
      </Card>

      <Card title={<span style={headStyle}>Private note — admins only</span>} style={CARD}>
        <p style={{ fontFamily: brand.fSans, fontSize: 12.5, color: brand.sub, marginTop: 0 }}>
          Members never see this note — not in the app, and not through the API. Change it from Edit.
        </p>
        {noteState.kind === 'loading' ? (
          <Skeleton active paragraph={{ rows: 2 }} title={false} />
        ) : noteState.kind === 'error' ? (
          <span style={muted}>The private note could not be loaded. Refresh to try again.</span>
        ) : noteState.note ? (
          <>
            <Typography.Paragraph data-testid="suggestion-admin-note" style={{ whiteSpace: 'pre-wrap' }}>
              {noteState.note.note}
            </Typography.Paragraph>
            <div style={{ fontFamily: brand.fSans, fontSize: 11.5, color: brand.sub }}>
              Last updated {fmtDate(noteState.note.updated_at)}
            </div>
          </>
        ) : (
          <span style={muted}>No note yet.</span>
        )}
      </Card>
    </Show>
  )
}
